import {ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Data, Params, Router} from "@angular/router";
import {AuthoringManagerService} from "../../../services/authoring-manager.service";
import {FieldServiceManagerService} from "../../../services/field-service-manager.service";
import {FieldService, FieldServiceType} from "../../../models/field-service";
import {UiManagerService} from "../../../services/ui-manager.service";
import {Location} from "../../../models/location";
import {CardAction} from "../../../components/scotty-card/scotty-card.component";
import {PhysicalAssetManagerService} from "../../../services/physical-asset-manager.service";
import {Paginator} from "../../../models/paginator";
import {Personnel} from "../../../models/personnel";
import {TableAction, TableColumn, TableColumnType, TableComponent} from "../../../components/table/table.component";
import {DetailCardComponent, DetailCardContent} from "../../../components/detail-card/detail-card.component";
import {environment} from "@environments/environment";
import {combineLatest} from "rxjs";
import {map} from "rxjs/operators";
import {PhysicalAsset} from "../../../models/physical-asset";
import {TranslatePipe} from "@ngx-translate/core";

enum CreationMode {
  NONE, NEW, UPDATE
}

@Component({
  selector: 'app-create-field-service-page',
  templateUrl: './create-field-service-page.component.html',
  styleUrls: ['./create-field-service-page.component.scss']
})
export class CreateFieldServicePageComponent implements OnInit {

  @ViewChild(DetailCardComponent, { static: false })
  detailCard: DetailCardComponent;

  @ViewChild('assetTable', { static: false })
  private assetTable: TableComponent;

  isLoading = true;

  mode: CreationMode = CreationMode.NONE;
  fieldServiceType: FieldServiceType;
  fieldService: FieldService;

  /* Children field services in current field service */
  childrenFieldServices: FieldService[];
  childrenFieldServicePaginator: Paginator<FieldService>;

  /* Physical assets in current field service */
  physicalAssets: PhysicalAsset[];
  physicalAssetsPaginator: Paginator<PhysicalAsset>;

  /* Personnel in current field service */
  personnel: Personnel[];
  personnelPaginator: Paginator<Personnel>;

  constructor(
    private fieldServiceManagerService: FieldServiceManagerService,
    private authoringManagerService: AuthoringManagerService,
    private physicalAssetManagerService: PhysicalAssetManagerService,
    private uiManagerService: UiManagerService,
    private cdr: ChangeDetectorRef,
    public route: ActivatedRoute,
    public router: Router,
    public translatePipe: TranslatePipe
  ) { }

  ngOnInit(): void {

    combineLatest([
      this.route.paramMap,
      this.route.data
      ],
    ).pipe(
      map(x => ({ params: x[0], data: x[1] }))
    ).subscribe(async (routeData: { params: Params; data: Data }) => {

      this.fieldServiceType = routeData.data.fieldServiceType as FieldServiceType;

      let fieldServiceId;
      switch (this.fieldServiceType) {
        case FieldServiceType.PLANT:
          fieldServiceId = routeData.params.get('plantId');
          break;
        case FieldServiceType.DEPARTMENT:
          fieldServiceId = routeData.params.get('departmentId');
          break;
        case FieldServiceType.PRODUCTION_LINE:
          fieldServiceId = routeData.params.get('productionLineId');
          break;
        case FieldServiceType.WORK_CENTER:
          fieldServiceId = routeData.params.get('workCenterId');
          break;
      }

      this.physicalAssetsPaginator = new Paginator<PhysicalAsset>();
      this.personnelPaginator = new Paginator<Personnel>();

      if (fieldServiceId) {

        // Fetch field service
        this.fieldService = await this.fieldServiceManagerService.getFieldService(fieldServiceId);

        // Create location if not exists
        this.fieldService.location = this.fieldService.location || new Location();

        // Get children field services in current field service
        if (this.fieldServiceType != FieldServiceType.WORK_CENTER) {
          this.childrenFieldServicePaginator = new Paginator<FieldService>();
          this.childrenFieldServicePaginator.onPageResults = (childrenFieldServices: FieldService[]) => {
            this.childrenFieldServices = childrenFieldServices;
          }
          await this.fieldServiceManagerService.getFieldServices(this.getChildFieldServiceType(), this.childrenFieldServicePaginator);
        }

        // Get assets in current field service
        this.physicalAssetsPaginator.onPageResults = (assets: PhysicalAsset[]) => {
          this.physicalAssets = assets;
        }
        await this.physicalAssetManagerService.getPhysicalAssetsFromFieldService(this.fieldService, null, this.physicalAssetsPaginator);

        // Get personnel in current field service
        this.personnelPaginator.onPageResults = (personnel: Personnel[]) => {
          this.personnel = personnel;
        }
        await this.fieldServiceManagerService.getUserFromFieldService(this.fieldService, this.personnelPaginator);

        this.mode = CreationMode.UPDATE;

      } else {

        // Initialize new field service
        this.fieldService = new FieldService();

        // Set parent production line if exists
        const parentFieldServiceId = routeData.params.get('workCenterId') ?? routeData.params.get('productionLineId') ?? routeData.params.get('departmentId') ?? routeData.params.get('plantId');
        if (parentFieldServiceId) {
          this.fieldService.productionLineId = parentFieldServiceId;
          this.fieldService.typeId = this.fieldServiceType;
        }

        this.physicalAssets = [];
        this.personnel = [];

        this.mode = CreationMode.NEW;

      }

      this.cdr.detectChanges();
      this.isLoading = false;

    });

  }

  getTypeLabel(type?: FieldServiceType, plural?: boolean) {
    switch (type ?? this.fieldServiceType) {
      case FieldServiceType.PLANT:
        return 'plant' + (plural ? 's' : '');
      case FieldServiceType.DEPARTMENT:
        return 'department' + (plural ? 's' : '');
      case FieldServiceType.PRODUCTION_LINE:
        return 'production line' + (plural ? 's' : '');
      case FieldServiceType.WORK_CENTER:
        return 'work center' + (plural ? 's' : '');
    }
  }

  getChildFieldServiceType() {
    switch (this.fieldServiceType) {
      case FieldServiceType.PLANT:
        return FieldServiceType.DEPARTMENT;
      case FieldServiceType.DEPARTMENT:
        return FieldServiceType.PRODUCTION_LINE;
      case FieldServiceType.PRODUCTION_LINE:
        return FieldServiceType.WORK_CENTER;
      case FieldServiceType.WORK_CENTER:
        return null;
    }
  }

  getCardTitle() {
    const typeLabel = this.getTypeLabel();
    switch (this.mode) {
      case CreationMode.NONE:
        return '&nbsp;';
      case CreationMode.NEW:
        return this.translatePipe.transform(`Create ${typeLabel}`);
      case CreationMode.UPDATE:
        return this.translatePipe.transform(`Manage ${typeLabel}`) + ` ${this.fieldService.name}`;
    }
  }

  getChildrenFieldServicesTableColumns(): TableColumn[] {
    return [
      { key: 'id', type: TableColumnType.DEFAULT, title: 'ID', isMasterKey: true },
      { key: 'name', type: TableColumnType.DEFAULT, title: 'Name' },
      { key: 'primary_address', type: TableColumnType.DEFAULT, title: 'Primary address' },
      { key: 'secondary_address', type: TableColumnType.DEFAULT, title: 'Secondary address' },
      { key: 'city', type: TableColumnType.DEFAULT, title: 'City' },
      { key: 'zip', type: TableColumnType.DEFAULT, title: 'Zip' },
      { key: 'province', type: TableColumnType.DEFAULT, title: 'Province initials' },
      { key: 'country', type: TableColumnType.DEFAULT, title: 'Country' },
      { key: 'actions', type: TableColumnType.ACTIONS, title: 'Actions' },
    ];
  }

  getChildrenFieldServicesTableData() {
    return (this.childrenFieldServices || []).map(childFieldService => {
      return {
        id: childFieldService.id,
        name: childFieldService.name,
        primary_address: childFieldService.location?.address,
        secondary_address: childFieldService.location?.address2,
        city: childFieldService.location?.city,
        zip: childFieldService.location?.zip,
        province: childFieldService.location?.provinceInitials,
        country: childFieldService.location?.country,
        actions: [
          {
            icon: 'delete',
            color: 'warn',
            callback: () => {
              this.uiManagerService.showConfirmDialog(`Remove ${this.getTypeLabel(this.getChildFieldServiceType())}`, `Are you sure do you want to remove this ${this.getTypeLabel(this.getChildFieldServiceType())}?<br>This operation is irreversible.`, async () => {
                await this.fieldServiceManagerService.removeFieldService(childFieldService);
                await this.childrenFieldServicePaginator.refresh();
              });
            }
          },
        ] as TableAction[]
      };
    });
  }

  getPhysicalAssetsTableColumns(): TableColumn[] {
    return [
      { key: 'serial_number', isMasterKey: true, type: TableColumnType.DEFAULT, title: 'Serial number' },
      { key: 'deployed_at', type: TableColumnType.DATE, title: 'Setup date' },
      { key: 'last_maintenance_at', type: TableColumnType.DATE, title: 'Last maintenance' },
      { key: 'last_maintenance_reason', type: TableColumnType.DEFAULT, title: 'Last maintenance description' },
      { key: 'actions', type: TableColumnType.ACTIONS, title: 'Actions' },
    ];
  }

  getPhysicalAssetsTableData() {
    return this.physicalAssets?.map(asset => {
      return {
        id: asset.id,
        serial_number: asset.serialNumber,
        deployed_at: new Date(asset.deployedAt),
        last_maintenance_at: asset.lastMaintenance ? new Date(asset.lastMaintenance.scheduledAt) : '-',
        last_maintenance_reason: asset.lastMaintenance ? asset.lastMaintenance.reason : '-',
        actions: [
          {
            icon: 'delete', color: 'warn', callback: () => {
              this.uiManagerService.showConfirmDialog('Remove asset', 'Are you sure do you want to remove this asset?<br>This operation is irreversible.', () => {
                this.physicalAssetManagerService.removePhysicalAsset(asset.id);
                this.physicalAssets.splice(this.physicalAssets.findIndex(x => x.id == asset.id), 1);
                this.cdr.detectChanges();
                this.assetTable.reloadData();
              });
            }
          }
        ] as TableAction[],
      };
    }) || [];
  }

  getPersonnelTableColumns(): TableColumn[] {
    return [
      { key: 'id', type: TableColumnType.DEFAULT, title: 'ID', isMasterKey: true },
      { key: 'first_name', type: TableColumnType.DEFAULT, title: 'First name' },
      { key: 'last_name', type: TableColumnType.DEFAULT, title: 'Last name' },
      { key: 'email', type: TableColumnType.DEFAULT, title: 'Email' },
      { key: 'phone_number', type: TableColumnType.DEFAULT, title: 'Phone number' },
      { key: 'actions', type: TableColumnType.ACTIONS, title: 'Actions' },
    ];
  }

  getPersonnelTableData() {
    return (this.personnel || []).map(person => {
      return {
        id: person.id,
        first_name: person.firstName,
        last_name: person.lastName,
        email: person.email,
        phone_number: person.phoneNumber,
        actions: [
          {
            icon: 'delete',
            color: 'warn',
            callback: () => {
              this.uiManagerService.showConfirmDialog('Remove personnel binding', 'Are you sure do you want to remove this personnel binding?<br>This operation is irreversible.', async () => {
                await this.fieldServiceManagerService.removeFieldServiceBind(this.fieldService, person);
                await this.personnelPaginator.refresh();
              });
            }
          },
        ] as TableAction[]
      };
    });
  }

  async save() {

    this.isLoading = true;

    switch (this.mode) {

      case CreationMode.NEW:
        this.fieldService = await this.fieldServiceManagerService.createFieldService(this.fieldService);
        await this.router.navigate(['..', this.fieldService.id], { relativeTo: this.route, replaceUrl: true });
        break;

      case CreationMode.UPDATE:
        await this.fieldServiceManagerService.updateFieldService(this.fieldService.id, this.fieldService);
        await this.router.navigate(['..'], { relativeTo: this.route });
        break;
    }

    this.isLoading = false;

  }

  async delete() {

    if (!this.canDelete()) {
      this.uiManagerService.showInfoDialog(this.translatePipe.transform('Cannot remove'), this.translatePipe.transform('You have to manually remove all the inner content before.'));
      return;
    }

    await this.uiManagerService.showConfirmDialog('Remove field service', 'Are you sure do you want to remove this field service?<br>This operation is irreversible.', async () => {
      await this.fieldServiceManagerService.removeFieldService(this.fieldService);
      await this.router.navigate(['..'], { relativeTo: this.route.parent });
    });
  }

  canDelete() {
    return this.childrenFieldServices.length == 0 && this.physicalAssets.length == 0 && this.personnel.length == 0;
  }

  getActions(): CardAction[] {
    return this.mode == CreationMode.UPDATE ? [
      {
        icon: 'qr_code',
        name: 'Download QR codes',
        disabled: this.physicalAssets.length == 0,
        callback: () => {
          this.physicalAssetManagerService.downloadQRCodes(this.fieldService);
        }
      },
    ] : [];
  }

  filterChildrenFieldServices(filter: string) {
    this.uiManagerService.notify(this.translatePipe.transform('Not implemented'));
  }

  filterPhysicalAsset(filter: string) {
    this.uiManagerService.notify(this.translatePipe.transform('Not implemented'));
  }

  filterPersonnel(filter: string) {
    this.uiManagerService.notify(this.translatePipe.transform('Not implemented'));
  }

  showChildFieldService(childFieldServiceId: string) {
    this.router.navigate([childFieldServiceId], { relativeTo: this.route });
  }

  async showPhysicalAsset(assetId: string) {
    const asset = await this.physicalAssetManagerService.getPhysicalAsset(assetId);
    this.router.navigate(['equipment', asset.model.id, 'asset', assetId]);
  }

  showPersonnel(personnelId: string) {
    this.router.navigate(['personnel', personnelId]);
  }

  get DetailCardContent() { return DetailCardContent; }

  getModes() { return CreationMode; }

  get environment() { return environment; }

  get FieldServiceType() { return FieldServiceType; }

}
